import { tt } from '@agos/shared'

import { customerDomainOf, type RegisteredDomain } from './customer-domain'

/**
 * GP webpay's return URL as the Tower shows it - `SAAS.md` §3.190, `BACKEND.md` §10A.444.
 *
 * **The path is the backend's `OnlinePaymentService.GP_RETURN_PATH`**, and the host is the franchise's customer
 * domain, because that is what `CustomerSite.base()` builds the URL from: the registry's primary domain. Shown so an
 * operator can check it against what GP webpay's Portal reports for a payment. `CardGatewaysAgreeTest` holds
 * the path to the Java constant.
 */
export const GP_WEBPAY_RETURN_PATH = '/api/v1/payments/gpwebpay/return'

/** The full URL for a customer domain, or null when the franchise has none - there is then nothing to show. */
export function gpWebpayReturnUrl(customerDomain: string | null): string | null {
  const domain = customerDomain?.trim() ?? ''
  if (domain === '') return null
  return `https://${domain.replace(/\/+$/, '')}${GP_WEBPAY_RETURN_PATH}`
}

/**
 * What the GP webpay card prints for the return URL - `SAAS.md` §3.191.
 *
 * **No primary domain is not no domain.** The server builds the URL from the primary registered domain and, with
 * none marked, falls back to the first that does not look like a manager's (§10A.359) - so *no customer domain*
 * was untrue of a franchise with domains and none marked. That case is said as what it is, and the address is not
 * guessed here: re-deriving the server's fallback would be a second spelling of a rule that decides where a paying
 * customer lands.
 */
export function gpWebpayReturnLine(domains: readonly RegisteredDomain[]): {
  url: string | null
  note: string | null
} {
  const url = gpWebpayReturnUrl(customerDomainOf(domains))
  if (url !== null) return { url, note: null }
  if (domains.length === 0) {
    return {
      url: null,
      note: tt('franšíza nemá žiadnu doménu - pridajte ju cez franchise-setup.sh (Domény), inak GP webpay nemá kam zákazníka vrátiť'),
    }
  }
  return {
    url: null,
    note: tt('žiadna doména nie je označená ako zákaznícka - AGOS pošle prvú, ktorá nevyzerá ako manažérska; zvoľte ju v detaile franšízy, nech je adresa istá'),
  }
}
